import { isNativeApp } from '@/lib/native';

/**
 * Saving generated files (CSV exports, itinerary PDFs).
 *
 * In a browser the file is downloaded through a temporary link. Inside the native app a `blob:`
 * download does nothing, so the file is written to the app's cache and handed to the share
 * sheet instead, where the user can save it to Files, AirDrop it or send it on.
 */
export function saveFile(blob: Blob, filename: string): Promise<void> {
  if (isNativeApp()) return shareFile(blob, filename);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  // The click must happen on an element in the DOM for Firefox to act on it.
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Released after the browser has started the download; a synchronous revoke cancels it in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return Promise.resolve();
}

const EXPORT_DIR = 'export';
const KEEP_SHARED_FILES_MS = 10 * 60 * 1000;

/** Removes shared files older than ten minutes. Called at app start and before each share. */
export async function cleanSharedFiles(): Promise<void> {
  const { Filesystem, Directory } = await import('@capacitor/filesystem');
  const { files } = await Filesystem.readdir({ path: EXPORT_DIR, directory: Directory.Cache }).catch(
    () => ({ files: [] }),
  );
  const limit = Date.now() - KEEP_SHARED_FILES_MS;
  await Promise.all(
    files
      .filter((f) => !(Number(f.name) > limit))
      .map((f) =>
        Filesystem.rmdir({
          path: `${EXPORT_DIR}/${f.name}`,
          directory: Directory.Cache,
          recursive: true,
        }).catch(() => {}),
      ),
  );
}

async function shareFile(blob: Blob, filename: string): Promise<void> {
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import('@capacitor/filesystem'),
    import('@capacitor/share'),
  ]);
  await cleanSharedFiles();
  const { uri } = await Filesystem.writeFile({
    path: `${EXPORT_DIR}/${Date.now()}/${filename.replace(/[\\/:]/g, '-')}`,
    data: await toBase64(blob),
    directory: Directory.Cache,
    recursive: true,
  });
  try {
    await Share.share({ files: [uri] });
  } catch (err) {
    if (!(err instanceof Error && /cancel/i.test(err.message))) throw err;
  }
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
